import { Formik } from 'formik';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';

import { API_BASE_URL } from '../../../globals';
import { fetcher } from '../../../utils';
import { PrimaryButton } from '../../button';
import { MessageSent } from './MessageSent';
import { WhiteWrapper } from './WhiteWrapper';

const InputStyles = css`
  margin: 0.25rem;
  width: 100%;
  background: white;
  color: #333;
  padding: 1rem 0.75rem;
  border: 2px solid #eee;
  border-radius: 0.25rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  font-family: ${({ theme }) => theme.fontFamily.regular};
  transition: all 0.2s ease-in-out;
  outline: none;

  &::placeholder {
    color: #333;
    opacity: 0.5;
  }

  &:focus {
    border: 2px solid ${({ theme }) => theme.colors.brandSecondary};
  }
`;

const Input = styled.input`
  ${InputStyles}
`;

const TextArea = styled.textarea`
  ${InputStyles};
  resize: none;
  min-height: 115px;
`;

const Select = styled.select`
  ${InputStyles};
  appearance: none;
  cursor: pointer;
  background: linear-gradient(45deg, transparent 50%, #808080 50%),
    linear-gradient(135deg, #808080 50%, transparent 50%), linear-gradient(to right, #fff, #fff);
  background-position: calc(100% - 1.5rem) calc(50% + 2px), calc(100% - 1.5rem + 6px) calc(50% + 2px), 100% 0;
  background-size: 6px 6px, 6px 6px, 2.6em 100%;
  background-repeat: no-repeat;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  margin-bottom: 0.25rem;
`;

const ErrorText = styled.p`
  color: red;
  margin: 0px 0 10px 7px;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
`;

const Form = styled.form`
  width: 100%;
`;

interface FormState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
}

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email address is required'),
  topic: Yup.string()
    .notOneOf([null, undefined, 'Select a topic'], 'Please select a topic')
    .required('Please select a topic'),
  text: Yup.string().min(1).required('Please provide a message'),
});

const initialValues = { email: '', topic: 'Select a topic', text: '' };

export const ContactForm: React.FC = () => {
  const [state, setState] = useState<FormState>({ status: 'idle' });

  const handleSubmit = async (values, actions) => {
    setState({ status: 'loading' });

    try {
      const result = await fetcher(`${API_BASE_URL}/contact-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (result.status === 200) {
        setState({ status: 'success' });
        actions.resetForm();
      } else {
        throw new Error(result.messages.join('. '));
      }
    } catch (error) {
      setState({ status: 'error', message: error.message });
    }
  };

  return (
    <WhiteWrapper>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => {
          if (state.status === 'success') {
            return <MessageSent onClose={() => setState({ status: 'idle' })} />;
          } else {
            return (
              <Form onSubmit={handleSubmit}>
                <Input
                  name="email"
                  placeholder="Your Email"
                  type="email"
                  value={values.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <ErrorText>{touched.email && errors.email ? String(errors.email) : ''}</ErrorText>
                <Select name="topic" value={values.topic} onBlur={handleBlur} onChange={handleChange}>
                  <option disabled>Select a topic</option>
                  <option value="Questions or Feedback">Questions or Feedback</option>
                  <option value="Payments / Account">Payments / Account</option>
                  <option value="Bug / Issue">Bug / Issue</option>
                </Select>
                <ErrorText>{touched.topic && errors.topic ? String(errors.topic) : ''}</ErrorText>
                <TextArea
                  name="text"
                  placeholder="Write your message here"
                  value={values.text}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <ErrorText>{touched.text && errors.text ? String(errors.text) : ''}</ErrorText>
                <ButtonWrapper>
                  <PrimaryButton
                    analyticsEvent={{ category: 'Contact Form', action: 'Send Message Button' }}
                    onClick={() => handleSubmit()}
                  >
                    Send message
                  </PrimaryButton>
                </ButtonWrapper>
              </Form>
            );
          }
        }}
      </Formik>

      {state.status === 'error' && state.message && <ErrorText>{state.message}</ErrorText>}
    </WhiteWrapper>
  );
};
